import { isVideoAssetId } from '@/entities/asset/model/assetMediaKind';
import { assetManifest } from '@/shared/config/assetManifest';
/** 阿里云 OSS 视频首帧处理参数，与旧资产库的视频卡片保持一致。 */
const OSS_VIDEO_FIRST_FRAME_PROCESS = 'x-oss-process=video/snapshot,t_0,f_jpg';
/** 时间轴单格截帧：160×90，时间由调用方按毫秒传入。不带 m_fast，取该时间点的画面。 */
const OSS_VIDEO_SNAPSHOT_PREFIX = 'x-oss-process=video/snapshot,t_';
/** 截帧输出格式和尺寸，拼在时间参数后面。 */
const OSS_VIDEO_SNAPSHOT_SUFFIX = ',f_jpg,w_160,h_90';
/** 去除空白并把空字符串统一为未配置。 */
function normalizeUrl(value) {
    const normalized = value?.trim();
    return normalized?.length ? normalized : undefined;
}
/** 返回素材类型对应的前端内置默认展示图。 */
export function getDefaultAssetThumbnail(type) {
    if (type === '2')
        return assetManifest.media.materialFallbacks.image;
    if (type === '3')
        return assetManifest.media.materialFallbacks.video;
    if (type === '4' || type === '5')
        return assetManifest.media.materialFallbacks.audio;
    return assetManifest.media.materialFallbacks.file;
}
/** 为历史视频构造 OSS 首帧地址；新上传视频会由后端写入同一 coverUrl。 */
export function buildVideoFirstFrameUrl(assetUrl) {
    const normalized = normalizeUrl(assetUrl);
    if (!normalized)
        return undefined;
    const separator = normalized.includes('?') ? '&' : '?';
    return `${normalized}${separator}${OSS_VIDEO_FIRST_FRAME_PROCESS}`;
}
/**
 * 为时间轴一格构造 OSS 截帧地址。
 * 只拼在素材播放地址上，不拼在已经带 t_0 的封面上。不传 m_fast，取该毫秒的画面而不是最近关键帧。本地 blob 不会发给 OSS。
 *
 * @param assetUrl 素材播放地址
 * @param timeMs 源视频时间，毫秒
 * @returns 截帧地址；没有可处理的远端地址时为空
 */
export function buildVideoSnapshotUrl(assetUrl, timeMs) {
    /** 去掉空白后的播放地址。 */
    const normalized = normalizeUrl(assetUrl);
    if (!normalized || normalized.startsWith('blob:') || normalized.startsWith('data:'))
        return undefined;
    /** 截帧时间，负值和小数都收成非负整数毫秒。 */
    const snapshotTimeMs = Math.max(0, Math.round(timeMs));
    /** 地址里已有查询参数时用 & 衔接，否则用 ?。 */
    const separator = normalized.includes('?') ? '&' : '?';
    return `${normalized}${separator}${OSS_VIDEO_SNAPSHOT_PREFIX}${String(snapshotTimeMs)}${OSS_VIDEO_SNAPSHOT_SUFFIX}`;
}
/** 素材缩略图主地址与最终默认图，供卡片、预览轨道和无文件占位统一使用。 */
export function resolveAssetThumbnail(asset) {
    const fallback = getDefaultAssetThumbnail(asset.assetDataType);
    const coverUrl = normalizeUrl(asset.coverUrl);
    const assetUrl = normalizeUrl(asset.asset);
    let primary;
    if (asset.assetDataType === '2') {
        primary = coverUrl ?? assetUrl;
    }
    else if (asset.assetDataType === '3') {
        primary = coverUrl ?? buildVideoFirstFrameUrl(assetUrl);
    }
    else {
        primary = coverUrl;
    }
    return {
        primary: primary ?? fallback,
        fallback,
        usesDefaultInitially: primary == null,
    };
}
/** 图片与音频的文件扩展名，用于从绝对地址推断类型；视频口径复用素材类型模块。 */
const IMAGE_EXTENSIONS = [
    'png',
    'jpg',
    'jpeg',
    'webp',
    'gif',
    'bmp',
    'avif',
    'svg',
];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'aac', 'm4a', 'ogg'];
/** 判断素材引用是否为绝对地址：平台默认素材以 URL 形式注入，而不是素材库 ID。 */
export function isAbsoluteAssetUrl(value) {
    const normalized = normalizeUrl(value);
    return normalized !== undefined && /^https?:\/\//i.test(normalized);
}
/** 按媒体类型取前端内置默认展示图。 */
export function getDefaultThumbnailByKind(kind) {
    if (kind === 'video')
        return assetManifest.media.materialFallbacks.video;
    if (kind === 'image')
        return assetManifest.media.materialFallbacks.image;
    return assetManifest.media.materialFallbacks.audio;
}
/**
 * 从绝对地址推断媒体类型。
 *
 * 素材位按协议保存可直接播放的地址，没有素材实体时扩展名是唯一可用的类型线索；
 * 视频判定复用素材类型模块的白名单，避免两处口径漂移。
 *
 * @param url 素材引用值
 * @returns 视频、图片或音频；非绝对地址或扩展名不可识别时返回 null
 */
export function resolveMediaKindFromUrl(url) {
    const normalized = normalizeUrl(url);
    if (!normalized || !/^https?:\/\//i.test(normalized))
        return null;
    if (isVideoAssetId(normalized))
        return 'video';
    /** 去掉查询与哈希，避免签名参数影响扩展名判断。 */
    const path = normalized.split('?')[0].split('#')[0].toLowerCase();
    if (IMAGE_EXTENSIONS.some((extension) => path.endsWith(`.${extension}`)))
        return 'image';
    if (AUDIO_EXTENSIONS.some((extension) => path.endsWith(`.${extension}`)))
        return 'audio';
    return null;
}
/**
 * 素材引用的缩略图来源。
 *
 * 素材位保存的是素材库 ID 或平台注入的绝对地址：
 * 命中文档里的素材就用实体封面（视频取封面或 OSS 首帧）；
 * 绝对地址按扩展名区分图片与视频，图片直接用地址、视频取首帧；
 * 解析不到时给类型默认图，保证界面上始终有可辨认的图，而不是空白。
 *
 * @param params 素材位引用、解析出的素材实体与预期媒体类型
 * @returns 主地址、兜底地址与是否一开始就使用默认图
 */
export function resolveReferenceThumbnail(params) {
    const fallback = getDefaultThumbnailByKind(params.kind);
    if (params.asset)
        return resolveAssetThumbnail(params.asset);
    const reference = normalizeUrl(params.reference);
    if (!reference || !/^https?:\/\//i.test(reference)) {
        return { primary: fallback, fallback, usesDefaultInitially: true };
    }
    /** 地址推断出的类型；识别不出时按调用方声明的类型处理。 */
    const urlKind = resolveMediaKindFromUrl(reference) ?? params.kind;
    /** 图片直接引用地址，视频取 OSS 首帧，其余（音频）只能用类型默认图。 */
    const primary = urlKind === 'image'
        ? reference
        : urlKind === 'video'
            ? (buildVideoFirstFrameUrl(reference) ?? fallback)
            : fallback;
    return { primary, fallback, usesDefaultInitially: primary === fallback };
}
